import React, { useState, useEffect, useCallback } from 'react';
import { whatsappAPI, studentsAPI } from '../services/api';
import { useYear } from '../context/YearContext';

// ── Template definitions ──────────────────────────────────────────────────────
const TEMPLATES = [
  {
    id:          'fee_reminder',
    label:       '💰 Fee Reminder',
    description: 'Send pending fee reminders to selected parents',
    perStudent:  true,
    filterClass: true,
    autoSelect:  'pending', // auto-select students with pending > 0
    extraFields: [],
    preview:     (extra, schoolName) =>
      `Hello [Father Name],\n\nThis is a gentle reminder that the school fee is pending for your child.\n\nStudent: [Student Name]\nClass: [Class]\nPending Amount: Rs.[Amount]\n\nKindly clear the dues at the earliest to avoid any inconvenience.\n\nThank you,\n${schoolName} School Management`,
  },
  {
    id:          'general_announcement',
    label:       '📢 General Announcement',
    description: 'Send any custom message to all selected parents',
    perStudent:  false,
    filterClass: true,
    autoSelect:  'all',
    extraFields: [
      { key: 'message', label: 'Your Message', type: 'textarea', placeholder: 'e.g. School will be closed tomorrow for elections. Stay safe.' },
    ],
    preview:     (extra, schoolName) =>
      `Dear Parent,\n\n${extra.message || '[Your message here]'}\n\nThank you,\n${schoolName} School Management`,
  },
  {
    id:          'exam_notice',
    label:       '📝 Exam Notice',
    description: 'Notify parents about upcoming exams',
    perStudent:  false,
    filterClass: true,
    autoSelect:  'all',
    extraFields: [
      { key: 'class_name', label: 'Class', type: 'text', placeholder: 'e.g. Class 9' },
      { key: 'start_date', label: 'Exam Start Date', type: 'text', placeholder: 'e.g. 5th May 2026' },
    ],
    preview:     (extra, schoolName) =>
      `Dear Parent,\n\nExaminations for Class ${extra.class_name || '[Class]'} are scheduled to begin from ${extra.start_date || '[Date]'}.\n\nKindly ensure your child is well prepared and present on time.\n\nThank you,\n${schoolName} School Management`,
  },
  {
    id:          'holiday_notice',
    label:       '🎉 Holiday Notice',
    description: 'Inform parents about school holidays',
    perStudent:  false,
    filterClass: false,
    autoSelect:  'all',
    extraFields: [
      { key: 'date',        label: 'Holiday Date',      type: 'text', placeholder: 'e.g. 1st May 2026' },
      { key: 'reason',      label: 'Reason',            type: 'text', placeholder: 'e.g. May Day' },
      { key: 'resume_date', label: 'Classes Resume On', type: 'text', placeholder: 'e.g. 2nd May 2026' },
    ],
    preview:     (extra, schoolName) =>
      `Dear Parent,\n\nPlease be informed that the school will remain closed on ${extra.date || '[Date]'} due to ${extra.reason || '[Reason]'}.\n\nClasses will resume on ${extra.resume_date || '[Resume Date]'}.\n\nThank you,\n${schoolName} School Management`,
  },
  {
    id:          'student_report',
    label:       '📊 Student Report',
    description: 'Send marks, grade and attendance to parents',
    perStudent:  true,
    filterClass: true,
    autoSelect:  'all',
    extraFields: [
      { key: 'exam_name',  label: 'Exam Name',              type: 'text',     placeholder: 'e.g. Half Yearly Exam' },
      { key: 'subjects',   label: 'Subject-wise Marks',     type: 'textarea', placeholder: 'e.g. Telugu: 85/100 (A)\nHindi: 78/100 (B+)\nEnglish: 92/100 (A+)' },
      { key: 'grade',      label: 'Overall Grade',          type: 'text',     placeholder: 'e.g. A+' },
      { key: 'attendance', label: 'Attendance Percentage',  type: 'text',     placeholder: 'e.g. 92%' },
    ],
    preview:     (extra, schoolName) =>
      `Hello [Father Name],\n\nPlease find below the academic report for your child.\n\nStudent: [Student Name]\nClass: [Class]\nExam: ${extra.exam_name || '[Exam Name]'}\n\nSubject-wise Marks:\n${extra.subjects || '[Subjects]'}\n\nOverall Grade: ${extra.grade || '[Grade]'}\nAttendance: ${extra.attendance || '[Attendance]'}\n\nFor details please visit the school app.\n\nThank you,\n${schoolName} School Management`,
  },
];

export default function WhatsAppBulk({ schoolName }) {
  const { year } = useYear();

  const [activeTemplate, setActiveTemplate] = useState(TEMPLATES[0]);
  const [classes,        setClasses]        = useState([]);
  const [selClass,       setSelClass]       = useState('__all__');
  const [students,       setStudents]       = useState([]);
  const [selected,       setSelected]       = useState(new Set());
  const [loading,        setLoading]        = useState(false);
  const [sending,        setSending]        = useState(false);
  const [results,        setResults]        = useState(null);
  const [error,          setError]          = useState('');
  const [extraParams,    setExtraParams]    = useState({});

  useEffect(() => {
    if (!year) return;
    studentsAPI.getClasses(year).then((r) => setClasses(r.data?.data || [])).catch(() => {});
  }, [year]);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError('');
    setResults(null);
    try {
      const params = { year };
      if (selClass !== '__all__') params.class = selClass;
      const res  = await whatsappAPI.preview(params);
      const data = res.data?.data || [];
      setStudents(data);
      // Auto-select based on template
      const next = new Set();
      data.forEach((s) => {
        if (!s.has_valid_phone) return;
        if (activeTemplate.autoSelect === 'pending' && s.pending_amount > 0) next.add(s.student_id);
        if (activeTemplate.autoSelect === 'all') next.add(s.student_id);
      });
      setSelected(next);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load students');
    } finally {
      setLoading(false);
    }
  }, [year, selClass, activeTemplate]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const handleTemplateSwitch = (tmpl) => {
    setActiveTemplate(tmpl);
    setExtraParams({});
    setResults(null);
    setError('');
  };

  const toggleSelect  = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const validStudents = students.filter((s) => s.has_valid_phone);
  const toggleAll     = () => {
    if (selected.size === validStudents.length) setSelected(new Set());
    else setSelected(new Set(validStudents.map((s) => s.student_id)));
  };

  const handleSend = async () => {
    if (selected.size === 0 && activeTemplate.perStudent) return;
    setSending(true);
    setError('');
    setResults(null);
    try {
      const res = await whatsappAPI.sendTemplate({
        template_name: activeTemplate.id,
        student_ids:   activeTemplate.perStudent ? Array.from(selected) : Array.from(selected),
        extra_params:  extraParams,
        year,
      });
      setResults(res.data?.data || {});
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send messages');
    } finally {
      setSending(false);
    }
  };

  const extraValid = activeTemplate.extraFields.every((f) => !!(extraParams[f.key] || '').trim());
  const canSend    = selected.size > 0 && extraValid && !sending;
  const fmtAmt     = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));
  const previewText = activeTemplate.preview(extraParams, schoolName || 'School');

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <h2 style={s.title}>📱 WhatsApp Bulk Messaging</h2>
        <p style={s.subtitle}>Send messages to parents via WhatsApp — powered by Gupshup</p>
      </div>

      {/* Template Quick Buttons */}
      <div style={s.templateBar}>
        <div style={s.templateLabel}>Select Message Type:</div>
        <div style={s.templateButtons}>
          {TEMPLATES.map((tmpl) => (
            <button
              key={tmpl.id}
              style={activeTemplate.id === tmpl.id ? s.tmplBtnActive : s.tmplBtn}
              onClick={() => handleTemplateSwitch(tmpl)}
            >
              {tmpl.label}
            </button>
          ))}
        </div>
        <div style={s.templateDesc}>{activeTemplate.description}</div>
      </div>

      <div style={s.body}>
        {/* Left — extra fields + preview */}
        <div style={s.leftPanel}>
          {/* Extra input fields */}
          {activeTemplate.extraFields.length > 0 && (
            <div style={s.card}>
              <div style={s.cardTitle}>✏️ Message Details</div>
              {activeTemplate.extraFields.map((f) => (
                <label key={f.key} style={s.fieldLabel}>
                  {f.label} <span style={{ color: '#c62828' }}>*</span>
                  {f.type === 'textarea' ? (
                    <textarea
                      style={s.textarea}
                      rows={4}
                      placeholder={f.placeholder}
                      value={extraParams[f.key] || ''}
                      onChange={(e) => setExtraParams((p) => ({ ...p, [f.key]: e.target.value }))}
                    />
                  ) : (
                    <input
                      style={s.input}
                      type="text"
                      placeholder={f.placeholder}
                      value={extraParams[f.key] || ''}
                      onChange={(e) => setExtraParams((p) => ({ ...p, [f.key]: e.target.value }))}
                    />
                  )}
                </label>
              ))}
            </div>
          )}

          {/* Message Preview */}
          <div style={s.card}>
            <div style={s.cardTitle}>👁️ Message Preview</div>
            <div style={s.phoneFrame}>
              <div style={s.phoneBubble}>
                {previewText.split('\n').map((line, i) => (
                  <span key={i}>{line}<br /></span>
                ))}
              </div>
              <div style={s.phoneTime}>2:30 PM ✓✓</div>
            </div>
          </div>
        </div>

        {/* Right — filters + student list */}
        <div style={s.rightPanel}>
          {/* Filters */}
          <div style={s.filtersRow}>
            {activeTemplate.filterClass && (
              <div style={s.filterGroup}>
                <label style={s.filterLabel}>Class</label>
                <select style={s.select} value={selClass} onChange={(e) => setSelClass(e.target.value)}>
                  <option value="__all__">All Classes</option>
                  {classes.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
            <div style={s.filterGroup}>
              <label style={s.filterLabel}>Year</label>
              <span style={s.yearBadge}>{year}</span>
            </div>
          </div>

          {/* Stats */}
          <div style={s.statsRow}>
            {[
              { n: students.length,      label: 'Total',       color: '#1a237e' },
              { n: validStudents.length, label: 'Valid Phone',  color: '#2e7d32' },
              { n: students.filter((s) => s.pending_amount > 0 && s.has_valid_phone).length, label: 'With Dues', color: '#e65100' },
              { n: selected.size,        label: 'Selected',    color: '#1565c0' },
            ].map(({ n, label, color }) => (
              <div key={label} style={s.stat}>
                <div style={{ ...s.statNum, color }}>{n}</div>
                <div style={s.statLabel}>{label}</div>
              </div>
            ))}
          </div>

          {error   && <div style={s.errorBanner}>{error}</div>}

          {/* Results */}
          {results && (
            <div style={s.resultsBanner}>
              <b>📊 Results — </b>
              <span style={{ color: '#2e7d32' }}>✅ Sent: {results.sent}</span> &nbsp;
              <span style={{ color: '#c62828' }}>❌ Failed: {results.failed}</span> &nbsp;
              <span style={{ color: '#e65100' }}>⚠️ Skipped: {results.skipped}</span>
              {results.details?.some((d) => d.status === 'failed') && (
                <details style={{ marginTop: 8 }}>
                  <summary style={{ cursor: 'pointer', color: '#c62828', fontSize: 13 }}>View failures</summary>
                  {results.details.filter((d) => d.status !== 'sent').map((d, i) => (
                    <div key={i} style={{ fontSize: 12, color: '#37474f', marginTop: 4 }}>
                      {d.student_name} — {d.reason || d.error}
                    </div>
                  ))}
                </details>
              )}
            </div>
          )}

          {/* Action row */}
          <div style={s.actionRow}>
            <button style={s.selectAllBtn} onClick={toggleAll}>
              {selected.size === validStudents.length ? '☐ Deselect All' : '☑ Select All'}
            </button>
            <button
              style={{ ...s.sendBtn, opacity: canSend ? 1 : 0.45, cursor: canSend ? 'pointer' : 'not-allowed' }}
              disabled={!canSend}
              onClick={handleSend}
            >
              {sending ? `Sending… (${selected.size})` : `📤 Send to ${selected.size} Parents`}
            </button>
          </div>

          {/* Student table */}
          {loading ? (
            <div style={s.center}>Loading…</div>
          ) : students.length === 0 ? (
            <div style={s.center}>No students found.</div>
          ) : (
            <div className="table-container" style={s.tableWrap}>
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={s.th}>☑</th>
                    <th style={s.th}>Student</th>
                    <th style={s.th}>Father</th>
                    <th style={s.th}>Class</th>
                    <th style={s.th}>Mobile</th>
                    <th style={s.th}>Pending (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map((st) => (
                    <tr key={st.student_id} style={{ ...s.tr, opacity: st.has_valid_phone ? 1 : 0.45 }}>
                      <td style={s.td}>
                        <input type="checkbox" checked={selected.has(st.student_id)} disabled={!st.has_valid_phone}
                          onChange={() => toggleSelect(st.student_id)} />
                      </td>
                      <td style={s.td}>{st.student_name}</td>
                      <td style={s.td}>{st.father_name}</td>
                      <td style={s.td}>{st.class}</td>
                      <td style={s.td}>
                        <span style={{ color: st.has_valid_phone ? '#2e7d32' : '#c62828' }}>
                          {st.mobile_number || 'No phone'}{!st.has_valid_phone && ' ⚠️'}
                        </span>
                      </td>
                      <td style={{ ...s.td, fontWeight: 600, color: st.pending_amount > 0 ? '#c62828' : '#2e7d32' }}>
                        ₹{fmtAmt(st.pending_amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  page:           { padding: 20, fontFamily: 'system-ui, sans-serif', maxWidth: 1200, margin: '0 auto' },
  header:         { marginBottom: 16 },
  title:          { margin: 0, fontSize: 22, color: '#1a237e' },
  subtitle:       { margin: '4px 0 0', color: '#607d8b', fontSize: 13 },
  templateBar:    { background: '#fff', borderRadius: 10, padding: 16, marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' },
  templateLabel:  { fontWeight: 700, fontSize: 13, color: '#37474f', marginBottom: 10 },
  templateButtons:{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 },
  tmplBtn:        { background: '#f5f7fa', color: '#37474f', border: '1.5px solid #e0e0e0', borderRadius: 20, padding: '7px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  tmplBtnActive:  { background: '#25d366', color: '#fff', border: '1.5px solid #25d366', borderRadius: 20, padding: '7px 16px', cursor: 'pointer', fontWeight: 700, fontSize: 13 },
  templateDesc:   { fontSize: 12, color: '#78909c' },
  body:           { display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' },
  leftPanel:      { flex: '0 0 320px', minWidth: 280, display: 'flex', flexDirection: 'column', gap: 16 },
  rightPanel:     { flex: 1, minWidth: 300 },
  card:           { background: '#fff', borderRadius: 10, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' },
  cardTitle:      { fontWeight: 700, fontSize: 14, color: '#1a237e', marginBottom: 12 },
  fieldLabel:     { display: 'flex', flexDirection: 'column', fontSize: 12, fontWeight: 600, color: '#37474f', gap: 4, marginBottom: 10 },
  input:          { border: '1px solid #cfd8dc', borderRadius: 6, padding: '8px 10px', fontSize: 14 },
  textarea:       { border: '1px solid #cfd8dc', borderRadius: 6, padding: '8px 10px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit' },
  phoneFrame:     { background: '#e5ddd5', borderRadius: 12, padding: 16, minHeight: 120 },
  phoneBubble:    { background: '#fff', borderRadius: '0 12px 12px 12px', padding: '10px 14px', fontSize: 13, lineHeight: 1.7, color: '#212121', maxWidth: 280, boxShadow: '0 1px 3px rgba(0,0,0,0.15)', whiteSpace: 'pre-wrap' },
  phoneTime:      { fontSize: 11, color: '#78909c', textAlign: 'right', marginTop: 4 },
  filtersRow:     { display: 'flex', gap: 16, alignItems: 'flex-end', marginBottom: 12, flexWrap: 'wrap' },
  filterGroup:    { display: 'flex', flexDirection: 'column', gap: 4 },
  filterLabel:    { fontSize: 12, fontWeight: 700, color: '#37474f' },
  select:         { border: '1px solid #cfd8dc', borderRadius: 6, padding: '7px 12px', fontSize: 14, minWidth: 150 },
  yearBadge:      { background: '#e3f2fd', color: '#1565c0', padding: '6px 14px', borderRadius: 6, fontWeight: 700, fontSize: 14 },
  statsRow:       { display: 'flex', gap: 20, marginBottom: 12, flexWrap: 'wrap' },
  stat:           { display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 },
  statNum:        { fontSize: 24, fontWeight: 700 },
  statLabel:      { fontSize: 10, color: '#607d8b' },
  errorBanner:    { background: '#ffebee', border: '1px solid #ef9a9a', borderRadius: 6, padding: '10px 14px', marginBottom: 10, color: '#c62828', fontSize: 13 },
  resultsBanner:  { background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 6, padding: '10px 14px', marginBottom: 10, fontSize: 14 },
  actionRow:      { display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' },
  selectAllBtn:   { background: '#e3f2fd', color: '#1565c0', border: '1px solid #90caf9', borderRadius: 6, padding: '8px 14px', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  sendBtn:        { background: '#25d366', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontWeight: 700, fontSize: 15 },
  tableWrap:      { background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)', overflow: 'auto' },
  table:          { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th:             { background: '#e3f2fd', color: '#1a237e', padding: '9px 12px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' },
  tr:             { borderBottom: '1px solid #eceff1' },
  td:             { padding: '9px 12px', color: '#37474f', verticalAlign: 'middle' },
  center:         { textAlign: 'center', padding: 40, color: '#607d8b' },
};
